/**
 * LocationPicker
 *
 * Campo amigável de localização geográfica para o ContactForm.
 *  - Mini-mapa MapLibre com marcador arrastável
 *  - Clique no mapa para posicionar o pino
 *  - Botão "Localizar pelo endereço" usa Nominatim (OSM, sem API key)
 *  - Exibe coordenadas formatadas quando definidas
 *  - Botão "Limpar" para remover as coordenadas
 */

import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPin, Search, X, Loader2, Navigation } from 'lucide-react';

// ── Tiles (mesmos do ContactMap, dark, sem API key) ───────────────────────────

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/dark';

const MAP_STYLE_FALLBACK: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm' }],
};

// ── Props ──────────────────────────────────────────────────────────────────────

export interface LocationPickerProps {
  latitude: number | null;
  longitude: number | null;
  /** Dica de endereço usada para geocodificação — ex: "Centro, Maceió, AL" */
  addressHint?: string;
  onChange: (lat: number | null, lng: number | null) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function round7(n: number) {
  return parseFloat(n.toFixed(7));
}

function fmtCoord(n: number) {
  return n.toFixed(5);
}

// ── Componente ─────────────────────────────────────────────────────────────────

export function LocationPicker({ latitude, longitude, addressHint, onChange }: LocationPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<maplibregl.Map | null>(null);
  const markerRef    = useRef<maplibregl.Marker | null>(null);

  const [geocoding, setGeocoding] = useState(false);
  const [geoError,  setGeoError]  = useState<string | null>(null);

  const hasCoords = latitude != null && longitude != null;

  // ── Inicializa o mapa ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const center: [number, number] = hasCoords ? [longitude!, latitude!] : [-47.9, -15.8];
    const zoom = hasCoords ? 14 : 4;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style:     MAP_STYLE,
      center,
      zoom,
      attributionControl: false,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('error', () => map.setStyle(MAP_STYLE_FALLBACK));

    /** Cria ou move o marcador arrastável */
    const placeMarker = (lat: number, lng: number) => {
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        const m = new maplibregl.Marker({ draggable: true, color: '#00C2FF' })
          .setLngLat([lng, lat])
          .addTo(map);
        m.on('dragend', () => {
          const ll = m.getLngLat();
          onChange(round7(ll.lat), round7(ll.lng));
        });
        markerRef.current = m;
      }
    };

    // Restaura marcador se já havia coords
    map.on('load', () => {
      if (latitude != null && longitude != null) {
        placeMarker(latitude, longitude);
      }
    });

    // Clique no mapa → posiciona pino
    map.on('click', (e) => {
      const lat = round7(e.lngLat.lat);
      const lng = round7(e.lngLat.lng);
      placeMarker(lat, lng);
      onChange(lat, lng);
    });

    // Cursor crosshair para indicar que é clicável
    map.getCanvas().style.cursor = 'crosshair';

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current  = null;
      markerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // monta uma vez

  // ── Sincroniza marcador quando lat/lng mudam externamente ──────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (latitude == null || longitude == null) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    const addMarker = () => {
      if (markerRef.current) {
        markerRef.current.setLngLat([longitude, latitude]);
      } else {
        const m = new maplibregl.Marker({ draggable: true, color: '#00C2FF' })
          .setLngLat([longitude, latitude])
          .addTo(map);
        m.on('dragend', () => {
          const ll = m.getLngLat();
          onChange(round7(ll.lat), round7(ll.lng));
        });
        markerRef.current = m;
      }
      map.easeTo({ center: [longitude, latitude], zoom: Math.max(map.getZoom(), 13), duration: 500 });
    };

    if (map.isStyleLoaded()) {
      addMarker();
    } else {
      map.once('load', addMarker);
    }
  // onChange é estável (vem do react-hook-form Controller), não precisa no dep
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  // ── Geocodificação via Nominatim ───────────────────────────────────────────

  const handleGeocode = async () => {
    const hint = addressHint?.trim();
    if (!hint) return;

    setGeocoding(true);
    setGeoError(null);

    try {
      const q   = encodeURIComponent(`${hint}, Brasil`);
      const url = `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=br`;
      const res = await fetch(url, {
        headers: {
          'Accept-Language': 'pt-BR,pt;q=0.9',
          'User-Agent':      'Lumenise/1.0 (lumenise.app)',
        },
      });
      const data: Array<{ lat: string; lon: string }> = await res.json();

      if (data.length > 0) {
        onChange(round7(parseFloat(data[0].lat)), round7(parseFloat(data[0].lon)));
      } else {
        setGeoError('Endereço não encontrado. Clique no mapa para definir manualmente.');
      }
    } catch {
      setGeoError('Erro ao geocodificar. Verifique a conexão e tente novamente.');
    } finally {
      setGeocoding(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">

      {/* Barra de ações */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleGeocode}
          disabled={!addressHint || geocoding}
          title={addressHint ? `Geocodificar: ${addressHint}` : 'Preencha Bairro, Cidade ou UF primeiro'}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold
                     bg-accent/10 text-accent hover:bg-accent/20 transition-colors
                     disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          {geocoding
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <Search className="h-3 w-3" />}
          Localizar pelo endereço
        </button>

        {hasCoords && (
          <>
            {/* Badge de coordenadas */}
            <span className="flex items-center gap-1.5 text-xs font-mono text-ink-muted
                             bg-midnight-2/60 border border-hairline rounded-lg px-2.5 py-1">
              <Navigation className="h-3 w-3 text-accent shrink-0" />
              {fmtCoord(latitude!)}°, {fmtCoord(longitude!)}°
            </span>

            {/* Limpar */}
            <button
              type="button"
              onClick={() => { onChange(null, null); setGeoError(null); }}
              className="flex items-center gap-1 text-xs text-ink-subtle
                         hover:text-danger transition-colors cursor-pointer"
            >
              <X className="h-3 w-3" />
              Limpar
            </button>
          </>
        )}
      </div>

      {/* Erro de geocodificação */}
      {geoError && (
        <p className="text-[11px] text-amber-400 flex items-start gap-1">
          <span className="shrink-0 mt-0.5">⚠</span> {geoError}
        </p>
      )}

      {/* Mini-mapa */}
      <div
        ref={containerRef}
        className="w-full rounded-xl overflow-hidden border border-hairline"
        style={{ height: 200 }}
      />

      {/* Dica de uso */}
      <p className="text-[11px] text-ink-subtle flex items-center gap-1">
        <MapPin className="h-3 w-3 text-accent shrink-0" />
        {hasCoords
          ? 'Arraste o pino ciano para ajustar a posição exata.'
          : 'Clique no mapa para definir a posição, ou use "Localizar pelo endereço".'}
      </p>
    </div>
  );
}
